import clsx from 'clsx'
import { PropsWithChildren, type FC } from 'react'
import { BasicStyledComponent, DSPageSections } from 'shared-definitions/types'
import { PageBodyContext, usePageBodyLayout } from '../contexts/PageBodyContext'
import styles from './TocLayoutAlt.module.css'
import TocMenu from './TocMenu'
import { useTocMonitoring } from '../hooks/use-toc-monitoring'
import { ScrollUpContext } from '../contexts/ScrollUpContext'
import { useInView } from 'react-intersection-observer'
import AltTableOfContentsHeading from './AltTableOfContentsHeading'
import SplitterLine from './SplitterLine'
import { ResponsiveContext } from '../contexts/ResponsiveContext'
import TocTopSlider from './TocTopSlider'

interface TocLayoutAltProps extends BasicStyledComponent, PropsWithChildren {
  tableOfContents: DSPageSections
}

const TocLayoutAlt: FC<TocLayoutAltProps> = ({ tableOfContents, className, children }) => {
  const { ready, lg } = ResponsiveContext.useContainer()
  const { ref, inView } = useInView()
  const { ref: contentRef, inView: contentInView } = useInView()
  const { current } = useTocMonitoring(tableOfContents ? tableOfContents.items : [])
  const { scrolledUp } = ScrollUpContext.useContainer()
  const { showReadingProgress } = PageBodyContext.useContainer()

  return (
    <div className={clsx(className, styles.container)} ref={contentRef}>
      <div className={styles.topMarker} ref={ref} />
      <aside className={clsx(styles.tocSidebar)}>
        {ready && lg && (
          <div
            data-table-of-content-wrapper
            className={clsx(styles.sidebarContent, {
              [styles.scrolledUp]: scrolledUp,
              [styles.showReadingProgress]: showReadingProgress,
              [styles.markerVisible]: inView,
            })}
          >
            <AltTableOfContentsHeading title={tableOfContents.title} className={styles.heading} />
            <SplitterLine className={styles.splitter} />
            <TocMenu currentSection={current} sectionItems={tableOfContents.items} />
          </div>
        )}
      </aside>
      {ready && !lg && (
        <div
          data-table-of-content-wrapper
          className={clsx(styles.mobileMenu, {
            [styles.mobileMenuOpen]: contentInView,
            [styles.scrolledUp]: scrolledUp,
            [styles.showReadingProgress]: showReadingProgress,
          })}
        >
          <AltTableOfContentsHeading title={tableOfContents.title} className={styles.heading} />
          <TocTopSlider
            activateTocScroll={contentInView}
            tableOfContentsItems={tableOfContents.items}
          />
        </div>
      )}
      <div data-content-wrapper ref={usePageBodyLayout()}>
        {children}
      </div>
    </div>
  )
}

export default TocLayoutAlt
